import type { AdAccountSummary, AdCampaignState, AdCampaignSummary, AdProviderKey } from '@agency/contracts';
import { Badge, type Tone } from '@agency/ui';
import { fmtDate } from '../../lib/format.ts';

/** Internal workflow state, in plain language. Provider delivery is shown separately and only when the provider reported it. */
export const STATE: Record<AdCampaignState, { label: string; tone: Tone; hint: string }> = {
  DRAFT: { label: 'Draft', tone: 'neutral', hint: 'Being prepared. Not visible to the provider.' },
  IN_REVIEW: { label: 'In review', tone: 'info', hint: 'Waiting for an Owner to approve this exact revision.' },
  APPROVED: { label: 'Approved, not published', tone: 'accent', hint: 'Approved for this revision. Nothing has been sent to the provider.' },
  PUBLISHING: { label: 'Sending to provider', tone: 'warning', hint: 'The request is with the provider. It is not confirmed yet.' },
  PUBLISHED: { label: 'Published', tone: 'success', hint: 'The provider acknowledged the campaign.' },
  FAILED: { label: 'Rejected by provider', tone: 'error', hint: 'The provider refused the campaign. Edit it to fix the listed problems.' },
  OUTCOME_UNKNOWN: { label: 'Outcome pending verification', tone: 'warning', hint: 'The provider did not answer. Check the outcome before doing anything else.' },
  ARCHIVED: { label: 'Archived', tone: 'neutral', hint: 'Kept for history.' },
};

export const PROVIDER_LABEL: Record<AdProviderKey, string> = { 'google-ads': 'Google Ads', 'linkedin-ads': 'LinkedIn Ads', 'dry-run': 'Dry run' };

export const StateBadge = ({ state }: { state: AdCampaignState }) => <Badge tone={STATE[state].tone}>{STATE[state].label}</Badge>;
export const DemoBadge = () => <Badge tone="neutral">Dry run, no real spend</Badge>;

export function DeliveryText({ campaign }: { campaign: Pick<AdCampaignSummary, 'deliveryStatus' | 'providerCampaignRef' | 'state'> }) {
  if (!campaign.deliveryStatus) return <span className="ag-muted">{campaign.state === 'PUBLISHED' ? 'Awaiting provider status' : 'Not at provider'}</span>;
  return <span className="ag-cell-2"><span>{campaign.deliveryStatus.toLowerCase().replace(/_/g, ' ')}</span><span className="ag-mono">{campaign.providerCampaignRef}</span></span>;
}

export const fmtWhen = fmtDate;

export const accountLabel = (a: Pick<AdAccountSummary, 'name' | 'provider' | 'currency'>) => `${a.name} · ${PROVIDER_LABEL[a.provider]} · ${a.currency}`;

export const capabilityRows = (c: AdAccountSummary['capabilities']): [string, boolean][] => [
  ['Create campaigns', c.createCampaign],
  ['Change budgets', c.updateBudget],
  ['Pause and resume', c.pauseCampaign],
  ['Read performance', c.performanceRead],
  ['Send conversions', c.offlineConversions],
];

/** One request id per user action is shown by QueryError; mutations surface ApiClientError fieldErrors through this helper. */
export function fieldMessages(error: { fieldErrors?: { field: string; message: string }[] } | null | undefined): string[] {
  return (error?.fieldErrors ?? []).map((f) => (f.field && f.field !== '(root)' ? `${f.message}` : f.message));
}

/** The provider name, except for the dry-run double where the demo badge already says it and repeating "Dry run" is noise. */
export const providerText = (c: { provider: AdProviderKey; isDemo: boolean }) => (c.isDemo ? null : PROVIDER_LABEL[c.provider]);
